<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding">
    <!-- Modal Header with Challenge Name -->
    <Teleport
      defer
      to="#portal-modal-header-target"
    >
      <h3 class="cf-title cf-title--3 cf-title--brand-primary">
        {{ challengeName }}
      </h3>
    </Teleport>

    <!-- Challenge Details Image (if available) -->
    <div
      v-if="challengeDetailsImage"
      class="cf-image-container cf-image-container--bleed"
    >
      <img
        alt="challenge details image"
        :src="challengeDetailsImage"
        class="cf-image"
        onerror="this.src='/images/image-not-found.png';"
      >
    </div>

    <div
      :class="[
        'cf-flex cf-flex--column cf-flex--gap-16 cf-text-align-left'
      ]"
    >
      <!-- Challenge Name and Description -->
      <div class="cf-flex cf-flex--column cf-flex--gap-12 cf-text-align-left">
        <p
          v-if="challengeDescription"
          class="cf-subtitle cf-subtitle--2"
        >
          {{ challengeDescription }}
        </p>
      </div>

      <!-- Available Rewards Section -->
      <template v-if="availableRewards.length">
        <div class="cf-flex cf-flex--column cf-flex--gap-12">
          <h4 class="cf-title cf-title--4 cf-title--gray">
            {{ availableRewardsTitle }}
          </h4>

          <div class="cf-flex cf-flex--column cf-flex--gap-8">
            <div
              v-for="reward in availableRewards"
              :key="reward.publicId"
              class="cf-reward-item"
              :class="{ 'cf-reward-item--completed': reward.isCompleted }"
            >
              <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-12">
                <div class="cf-reward-checkpoint">
                  <div class="cf-checkpoint-container">
                    <span
                      v-if="reward.isCompleted"
                      class="cf-checkmark"
                    >
                      ✓
                    </span>
                  </div>
                </div>
                <div class="cf-flex cf-flex--column cf-flex--gap-4">
                  <p class="cf-subtitle cf-subtitle--3">
                    {{ reward.requirementText }} - {{ reward.title }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Progress Bar Section -->
      <div class="cf-flex cf-flex--column cf-flex--gap-12">
        <cf-progress-bar
          :current-value="currentBalance"
          :max-value="finalTarget"
          :min-value="0"
          :thresholds="progressBarThresholds"
          :format-value="formatThresholdAmount"
          show-current-indicator
        />
      </div>

      <!-- Terms and Conditions Section -->
      <template v-if="hasTermsOrExpiration">
        <div class="cf-flex cf-flex--column cf-flex--gap-12">
          <h4 class="cf-title cf-title--4 cf-title--gray">
            Terms & Conditions
          </h4>

          <cf-formatted-content
            v-if="challengeTerms"
            class="cf-subtitle cf-subtitle--2"
            :content="challengeTerms"
          />

          <p
            v-if="expirationText"
            class="cf-subtitle cf-subtitle--3 cf-text-italics"
          >
            {{ expirationText }}
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';

  import CfFormattedContent from '@/view/components/globals/cf-formatted-content/index.vue';
  import CfProgressBar from '@/view/components/shared/cf-progress-bar/index.vue';

  export default {
    name: 'ChallengeDetails',

    components: {
      CfFormattedContent,
      CfProgressBar
    },

    props: {
      data: {
        type: Object,
        required: true
      }
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('offers', ['loyalty']),

      challenge() {
        return this.data.challenge;
      },

      challengeName() {
        return this.data.challengeName || this.challenge.currency?.longDescription || 'Challenge';
      },

      challengeDescription() {
        return this.data.challengeDescription || this.challenge.currency?.description || '';
      },

      challengeDetailsImage() {
        return this.challenge.currency?.challengeSummaryCardImagePath || null;
      },

      currentBalance() {
        return this.challenge.currentBalance || 0;
      },

      isSpendChallenge() {
        return this.challenge.currency?.accumulationType === 'Spend';
      },

      finalTarget() {
        return this.challenge.finalChallengeThreshold?.cost || 0;
      },


      progressBarThresholds() {
        if (!this.challenge.isMultiTieredChallenge || !this.challenge.allChallengeThresholds) {
          return [];
        }

        // Transform challenge thresholds to generic progress bar format
        return [...this.challenge.allChallengeThresholds]
          .sort((a, b) => a.cost - b.cost)
          .map(threshold => ({
            id: threshold.publicId,
            value: threshold.cost,
            isCompleted: this.currentBalance >= threshold.cost
          }));
      },

      availableRewards() {
        if (!this.challenge.allChallengeThresholds) return [];

        return this.challenge.allChallengeThresholds.map((threshold) => {
          const isCompleted = this.currentBalance >= threshold.cost;
          let title = '';
          let requirementText = '';

          if (threshold.rewardOffer?.title) {
            title = threshold.rewardOffer.title;
            requirementText = `$${threshold.cost}`;
          }
          else if (threshold.giftCardReward?.amount) {
            title = `$${threshold.giftCardReward.amount} Gift Card`;
            requirementText = `${threshold.cost}`;
          }
          else {
            title = 'Reward Available';
            if (this.isSpendChallenge) {
              requirementText = `$${threshold.cost}`;
            }
            else {
              requirementText = `Earn ${threshold.cost} ${this.challenge.currency?.pluralShortNameUpperCase || 'points'}`;
            }
          }

          return {
            publicId: threshold.publicId,
            title,
            requirementText,
            isCompleted
          };
        });
      },

      availableRewardsTitle() {
        return this.content?.Challenges?.metadata?.availableRewards || 'Available Rewards:';
      },

      challengeTerms() {
        return this.challenge.currency?.longDescription;
      },

      expirationText() {
        if (!this.challenge.challengeEndDate) return '';

        const endDate = new Date(this.challenge.challengeEndDate);
        return `Expires ${endDate.toLocaleDateString()}`;
      },

      hasTermsOrExpiration() {
        return this.challengeTerms || this.expirationText;
      }
    },

    methods: {
      formatThresholdAmount(amount) {
        if (this.isSpendChallenge) {
          return `$${amount}`;
        }
        return `${amount} ${this.challenge.currency?.pluralShortNameUpperCase || 'pts'}`;
      }
    }
  };
</script>

<style lang="scss" scoped>

  .cf-reward-item {
    border-radius: 8px;
    border: 1px solid var(--color-gray-light);

    &--completed {
      color: var(--color-brand-primary);

      .cf-subtitle {
        color: var(--color-success-dark);
      }
    }

    .cf-reward-checkpoint {
      .cf-checkpoint-container {
        width: 12px;
        height: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .cf-checkmark {
        font-size: 14px;
        line-height: 1;
        color: var(--color-brand-primary);
      }
    }
  }
</style>
